import client from "services/ngage/api-client";
import {Event, EventAPIData} from "types/Event";
import DataPage from "types/DataPage";
import {DateTime} from "luxon";
import deserializeEvent from "./deserializers/event";
import {queryString} from "services/string";
import {snakeKeys} from "../array";
import DataResource from "../../types/DataResource";
import {AxiosRequestConfig} from "axios";

export type EventFilters = {
    start?: DateTime,
    end?: DateTime,
    page?: number,
    perPage?: number,
    siteId?: number | null,
    typeId?: number | null,
    search?: string | null
    params?: {}
}

/**
 * Get all events (paginated)
 */
const all = async (page: number = 1, perPage: number = 1000) => {
    const query = queryString({page, per_page: perPage, sort: '-starts_at'})
    const response = await client.get<DataPage<Event> | null>(`/events?${query}`)

    if(response.data){
        response.data.data = response.data.data.map(deserializeEvent)
    }

    return response.data
}

/**
 * Get all events (paginated)
 */
const filtered = async (filters: EventFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))
    const response = await client.get<DataResource<Event>>('/events', {params})

    if (response.data) {
        response.data.data = response.data.data.map(deserializeEvent)
    }

    return response.data
}

/**
 * Get events between two dates (paginated)
 */
const between = async (start: DateTime, end: DateTime, page: number = 1, perPage: number = 100, siteId: number | null, typeId: number | null, requestConfig: AxiosRequestConfig = {}) => {
    const query = queryString({
        between: `${start.toUTC().toISO()},${end.toUTC().toISO()}`,
        page,
        per_page: perPage,
        sort: '-starts_at',
        sites: siteId ?? undefined,
        types: typeId ?? undefined,
    })

    const response = await client.get<DataPage<Event> | null>(`/events?${query}`, requestConfig)

    if(response.data){
        response.data.data = response.data.data.map(deserializeEvent)
    }

    return response.data
}

/**
 * Get a single event
 *
 * @param {number} id
 */
const get = async (id: number) => {
    const response = await client.get<Event>(`/events/${id}`)
    return response.data? deserializeEvent(response.data) : null
}

/**
 * Store a new event
 *
 * @param data
 */
const store = async (data: EventAPIData) => {
    const response = await client.post<Event>('/events', data)
    return response.data? deserializeEvent(response.data) : null
}

/**
 * Update an existing event
 *
 * @param {number} id
 * @param data
 * @param action
 */
const update = async (id: number, data: EventAPIData) => {
    const response = await client.put<Event>(`/events/${id}`, data)
    return response.data? deserializeEvent(response.data) : null
}

/**
 * Permanently delete a event
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    const response = await client.delete<null>(`/events/${id}`)
    return response.status === 204
}

const events = {
    all,
    filtered,
    between,
    get,
    store,
    update,
    destroy
}

export default events;